import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { Loader } from '../Loader/Loader';
import { ReactComponent as MinusIcon } from '../../assets/minus.svg';
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';
import styles from './PdfViewer.module.scss';

// The worker is copied verbatim into public/ (scripts/copy-pdf-worker.js):
// letting webpack bundle it rewrites its imports with absolute build paths
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const PAGE_MAX_WIDTH = 1000;
const EAGER_PAGES = 3;
const PAGE_RATIO = Math.SQRT2;
const RESIZE_SETTLE_MS = 100;

// Pages render lazily as they approach the viewport, so a long document does
// not block the main thread rendering everything at once
const LazyPage: React.FC<{
  pageNumber: number;
  width: number;
  root: HTMLDivElement | null;
}> = ({ pageNumber, width, root }) => {
  const holderRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(pageNumber <= EAGER_PAGES);

  useEffect(() => {
    const element = holderRef.current;
    if (!element || visible) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && setVisible(true)),
      { root, rootMargin: '600px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible, root]);

  return (
    <div
      ref={holderRef}
      className={styles.pdfPage}
      style={visible ? undefined : { minHeight: width * PAGE_RATIO }}
    >
      {visible && <Page pageNumber={pageNumber} width={width} />}
    </div>
  );
};

// `file` is whatever react-pdf accepts: a URL the browser can GET directly, or
// a Blob when the bytes had to be fetched with auth headers first
export const PdfViewer: React.FC<{ file: string | Blob; onFailed: () => void }> = ({ file, onFailed }) => {
  const { t } = useTranslation();
  const pagesRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);

  // Every width change makes react-pdf blank and redraw each visible page, so
  // transient resizes (the host panel sliding open, a scrollbar appearing) are
  // collapsed into one redraw once the size settles. The first measurement is
  // applied at once so pages can start rendering as soon as the document loads.
  useEffect(() => {
    const element = pagesRef.current;
    if (!element) return;
    const settle = debounce(setContainerWidth, RESIZE_SETTLE_MS);
    let measured = false;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (measured) {
        settle(width);
      } else {
        measured = true;
        setContainerWidth(width);
      }
    });
    observer.observe(element);
    return () => {
      settle.cancel();
      observer.disconnect();
    };
  }, []);

  const pageWidth =
    containerWidth !== null ? Math.min(containerWidth - 32, PAGE_MAX_WIDTH) * zoom : null;

  return (
    <div className={styles.pdfViewer}>
      <div className={styles.pdfToolbar}>
        <button
          type="button"
          className={styles.pdfZoomButton}
          aria-label={t('Zoom out') as string}
          disabled={zoom <= MIN_ZOOM}
          onClick={() => setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP))}
        >
          <MinusIcon className={styles.pdfZoomIcon} aria-hidden="true" />
        </button>
        <button type="button" className={styles.pdfZoomValue} onClick={() => setZoom(1)}>
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          className={styles.pdfZoomButton}
          aria-label={t('Zoom in') as string}
          disabled={zoom >= MAX_ZOOM}
          onClick={() => setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP))}
        >
          <PlusIcon className={styles.pdfZoomIcon} aria-hidden="true" />
        </button>
        {numPages !== null && (
          <span className={styles.pdfPageCount}>
            {numPages} {t('pages')}
          </span>
        )}
      </div>
      <div ref={pagesRef} className={styles.pdfPages}>
        <Document
          file={file}
          className={styles.pdfDocument}
          onLoadSuccess={(document) => setNumPages(document.numPages)}
          onLoadError={onFailed}
          loading={<Loader className={styles.pdfLoader} />}
          error={<span />}
        >
          {pageWidth !== null &&
            numPages !== null &&
            Array.from({ length: numPages }, (_, index) => (
              <LazyPage
                key={index + 1}
                pageNumber={index + 1}
                width={pageWidth}
                root={pagesRef.current}
              />
            ))}
        </Document>
      </div>
    </div>
  );
};
